import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { ParsedUrlQuery } from 'querystring'
import type { MovementPage } from '@/lib/inventory/queries'

import type {
    MovementPeriod,
    MovementType,
    Settings
} from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'

import {
    ArrowLeftRight,
    ChevronLeft,
    ChevronRight,
    FilterX,
    SlidersHorizontal
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Button, buttonVariants } from '@/components/ui/button'
import { usePageLoading } from '@/hooks/use-page-loading'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { useSettings } from '@/components/generals/settings-provider'
import { useInventoryActions } from '@/components/inventory/inventory-actions'
import { MOVEMENT_ICONS } from '@/components/inventory/movement-type-badge'
import { describeError } from '@/lib/db'
import { getCatalog, getMovements, getSettings } from '@/lib/inventory/queries'
import PageHeader from '@/components/generals/page-header'
import SegmentedControl from '@/components/generals/segmented-control'
import SectionCard from '@/components/generals/section-card'
import EmptyState from '@/components/generals/empty-state'
import LoadError from '@/components/generals/load-error'
import StatTile from '@/components/generals/stat-tile'
import MovementList from '@/components/inventory/movement-list'

import {
    DEFAULT_SETTINGS,
    MOVEMENT_PERIODS,
    MOVEMENT_TYPE_META,
    MOVEMENT_TYPES
} from '@/constants/inventory'

type MovementFiltersState = {
    type: MovementType | null
    productId: string | null
    period: MovementPeriod
    page: number
}

type ProductOption = {
    id: string
    name: string
    sku: string | null
}

type MovementsData = {
    result: MovementPage
    products: ProductOption[]
}

interface MovementsPageProps {
    settings: Settings
    data: MovementsData | null
    filters: MovementFiltersState
}

type TypeFilter = MovementType | 'todos'

const TYPE_FILTERS = [
    { value: 'todos' as TypeFilter, label: 'Todos' },
    ...MOVEMENT_TYPES.map(type => ({
        value: type as TypeFilter,
        label: MOVEMENT_TYPE_META[type].plural,
        icon: MOVEMENT_ICONS[type]
    }))
]

const readText = (value: string | string[] | undefined): string => typeof value === 'string' ? value.slice(0, 80) : ''

const readMovementFilters = (query: ParsedUrlQuery): MovementFiltersState => {
    const type = readText(query.tipo) as MovementType
    const period = readText(query.periodo) as MovementPeriod
    const page = Number.parseInt(readText(query.pagina), 10)

    return {
        type: MOVEMENT_TYPES.includes(type) ? type : null,
        productId: readText(query.producto) || null,
        period: MOVEMENT_PERIODS.some(option => option.value === period) ? period : 'all',
        page: Number.isFinite(page) && page > 0 ? page : 1
    }
}

const toQuery = (filters: MovementFiltersState): Record<string, string> => {
    const query: Record<string, string> = {}

    if (filters.type) query.tipo = filters.type
    if (filters.productId) query.producto = filters.productId
    if (filters.period !== 'all') query.periodo = filters.period
    if (filters.page > 1) query.pagina = String(filters.page)

    return query
}

const MovementsView: FC<{ data: MovementsData, filters: MovementFiltersState }> = ({ data, filters }): ReactNode => {
    const router = useRouter()
    const loading = usePageLoading()
    const { number, signed } = useSettings()
    const { openMovement } = useInventoryActions()
    const { result, products } = data
    const { totals } = result

    const hasFilters = Boolean(filters.type || filters.productId || filters.period !== 'all')
    const selectedProduct = products.find(product => product.id === filters.productId)

    const navigate = (patch: Partial<MovementFiltersState>) => {
        const next = { ...filters, page: 1, ...patch }

        void router.push({ pathname: '/movimientos', query: toQuery(next) }, undefined, { scroll: false })
    }

    const pageHref = (page: number) => ({ pathname: '/movimientos', query: toQuery({ ...filters, page }) })

    return (
        <div className='flex flex-col gap-6'>
            <div className='grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4'>
                <StatTile
                    label='Movimientos'
                    value={number(totals.count)}
                    hint={hasFilters ? 'Con los filtros aplicados' : 'Registrados en total'}
                    icon={ArrowLeftRight}
                />

                <StatTile
                    label='Unidades que entraron'
                    value={totals.unitsIn ? `+${number(totals.unitsIn)}` : '0'}
                    hint='Compras, devoluciones y altas'
                    icon={MOVEMENT_ICONS.entrada}
                    tone='in'
                />

                <StatTile
                    label='Unidades que salieron'
                    value={totals.unitsOut ? `−${number(totals.unitsOut)}` : '0'}
                    hint='Ventas, consumo y mermas'
                    icon={MOVEMENT_ICONS.salida}
                    tone='out'
                />

                <StatTile
                    label='Ajustes por recuento'
                    value={signed(totals.adjustNet)}
                    hint='Diferencia neta corregida'
                    icon={SlidersHorizontal}
                />
            </div>

            <SectionCard
                title='Historial'
                description={selectedProduct ? 'Solo los movimientos del producto elegido.' : 'Del más reciente al más antiguo.'}
                bodyClassName='p-0 sm:p-0'
                action={hasFilters ? (
                    <Button variant='ghost' size='sm' onClick={() => navigate({ type: null, productId: null, period: 'all' })}>
                        <FilterX aria-hidden='true' />
                        Quitar filtros
                    </Button>
                ) : undefined}
            >
                <div className='flex flex-col gap-3 border-b p-3 sm:p-4 lg:flex-row lg:items-center'>
                    <SegmentedControl
                        label='Filtrar por tipo de movimiento'
                        value={(filters.type ?? 'todos') as TypeFilter}
                        options={TYPE_FILTERS}
                        onChange={value => navigate({ type: value === 'todos' ? null : value })}
                        className='self-start'
                    />

                    <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:ml-auto lg:flex'>
                        <div>
                            <label htmlFor='movement-product' className='sr-only'>
                                Filtrar por producto
                            </label>

                            <NativeSelect
                                id='movement-product'
                                value={filters.productId ?? ''}
                                onChange={event => navigate({ productId: event.target.value || null })}
                                className='w-full lg:w-64'
                            >
                                <NativeSelectOption value=''>
                                    Todos los productos
                                </NativeSelectOption>

                                {filters.productId && !selectedProduct && (
                                    <NativeSelectOption value={filters.productId}>
                                        Producto eliminado
                                    </NativeSelectOption>
                                )}

                                {products.map(product => (
                                    <NativeSelectOption key={product.id} value={product.id}>
                                        {product.sku ? `${product.name} (${product.sku})` : product.name}
                                    </NativeSelectOption>
                                ))}
                            </NativeSelect>
                        </div>

                        <div>
                            <label htmlFor='movement-period' className='sr-only'>
                                Filtrar por fecha
                            </label>

                            <NativeSelect
                                id='movement-period'
                                value={filters.period}
                                onChange={event => navigate({ period: event.target.value as MovementPeriod })}
                                className='w-full lg:w-48'
                            >
                                {MOVEMENT_PERIODS.map(option => (
                                    <NativeSelectOption key={option.value} value={option.value}>
                                        {option.label}
                                    </NativeSelectOption>
                                ))}
                            </NativeSelect>
                        </div>
                    </div>
                </div>

                <div aria-busy={loading} className={cn('transition-opacity', loading && 'opacity-60')}>
                    {result.movements.length ? (
                        <MovementList movements={result.movements} />
                    ) : hasFilters ? (
                        <EmptyState
                            icon={FilterX}
                            title='No hay movimientos con estos filtros'
                            description='Prueba con otro producto, otro tipo o un periodo más amplio.'
                            action={(
                                <Button variant='outline' onClick={() => navigate({ type: null, productId: null, period: 'all' })}>
                                    Quitar filtros
                                </Button>
                            )}
                            className='py-14'
                        />
                    ) : (
                        <EmptyState
                            icon={ArrowLeftRight}
                            title='Todavía no hay movimientos'
                            description='Cada entrada, salida o ajuste que registres aparecerá aquí con su fecha y el stock resultante.'
                            action={(
                                <Button onClick={() => openMovement()}>
                                    <ArrowLeftRight aria-hidden='true' />
                                    Registrar movimiento
                                </Button>
                            )}
                            className='py-14'
                        />
                    )}
                </div>

                {result.pageCount > 1 && (
                    <nav aria-label='Paginación' className='flex items-center justify-between gap-3 border-t px-4 py-3 sm:px-5'>
                        <p className='text-sm text-muted-foreground'>
                            Página <span className='font-medium text-foreground tabular-nums'>{result.page}</span> de <span className='tabular-nums'>{result.pageCount}</span>
                        </p>

                        <div className='flex items-center gap-2'>
                            {result.page > 1 ? (
                                <Link href={pageHref(result.page - 1)} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                                    <ChevronLeft aria-hidden='true' />
                                    Anterior
                                </Link>
                            ) : (
                                <span className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'pointer-events-none opacity-50')} aria-disabled='true'>
                                    <ChevronLeft aria-hidden='true' />
                                    Anterior
                                </span>
                            )}

                            {result.page < result.pageCount ? (
                                <Link href={pageHref(result.page + 1)} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                                    Siguiente
                                    <ChevronRight aria-hidden='true' />
                                </Link>
                            ) : (
                                <span className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'pointer-events-none opacity-50')} aria-disabled='true'>
                                    Siguiente
                                    <ChevronRight aria-hidden='true' />
                                </span>
                            )}
                        </div>
                    </nav>
                )}
            </SectionCard>
        </div>
    )
}

const MovementsPage: FC<MovementsPageProps> = ({ data, filters }): ReactNode => {
    const { openMovement } = useInventoryActions()

    return (
        <Layout>
            <Seo
                title='Movimientos'
                description='Historial de entradas, salidas y ajustes de stock con su fecha, motivo y stock resultante.'
            />

            <div className='flex flex-col gap-6'>
                <PageHeader
                    title='Movimientos'
                    description='El registro de todo lo que entra y sale de tu almacén. Filtra por tipo, producto o fecha.'
                    actions={(
                        <Button onClick={() => openMovement()}>
                            <ArrowLeftRight aria-hidden='true' />
                            Registrar movimiento
                        </Button>
                    )}
                />

                {data ? <MovementsView data={data} filters={filters} /> : <LoadError />}
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<MovementsPageProps> = async ({ query }) => {
    const filters = readMovementFilters(query)

    try {
        const [settings, result, catalog] = await Promise.all([getSettings(), getMovements(filters), getCatalog()])

        const products = catalog.products.map(product => ({ id: product.id, name: product.name, sku: product.sku }))

        return { props: { settings, data: { result, products }, filters: { ...filters, page: result.page } } }
    }
    catch (error) {
        console.error('[movimientos] No se pudo cargar el historial:', describeError(error))

        return { props: { settings: DEFAULT_SETTINGS, data: null, filters } }
    }
}

export default MovementsPage
